import React, { useEffect, useCallback, useState } from 'react';
import './Styles.css';
import TextInput from '../../../../../Elements/ids/InputElements/TextInput';
import { formatMessage } from '../../../../../Localization/formatMessage';
import Messages from '../../../../../Localization/Messages';
import BankAccountTypeSelector from '../../../../../Elements/BankAccountTypeSelector';
import TeamInfoButtons from './Buttons/';
import { PresentationMode } from '../../../../../Enums/PresentationMode';
import {
  areFieldsValid,
  canEditBankAccount,
  canFormBeSubmitted,
  getChangedFieldsForTeam,
  getTeamResponsibleTypeOptions,
  isBankAccountMandatory,
  mapEditedTeamDataForApi,
  mapTeamToTeamData,
  validateCodePrefixInput,
  validateUserIdInput,
} from './Functions';
import { ServiceClient } from '../../../../../Services/ServiceClient';
import ErrorMessage from '../../../../../Elements/ErrorMessage';
import { createServerErrorMessage } from '../../../../../Helpers/FormInputHelper';
import Form from '../../../../../Elements/Form';
import { useNavigate } from 'react-router-dom';
import { teamPageReducerTypes } from '../../../Reducer';
import { ResultErrorCode } from '../../../../../Enums/ResultErrorCode';
import {
  areDecoupledFunctionsAvailableFunction,
  canTeamMobileNumberBeSet,
  canTeamNameBeSet,
  canTeamResponsibleTypeBeSet,
} from '../../../Functions';
import { useDebouncedEffect, useLegalUnitName } from '../../../../../Helpers/CustomHooks';
import ParentTeamCodePrefix from './ParentTeamCodePrefix/ParentTeamCodePrefix';
import DraggableModal from '../../../../../Elements/DraggableModal';
import { ResponsibleType } from '../../../../../Enums/ResponsibleType';
import Select from '../../../../../Elements/ids/InputElements/Select';
import { useGlobalContext } from '../../../../../context/GlobalContext';

export const teamInfoFormId = 'teamInfoForm';

const TeamInfo = ({ closeView, teamPageDispatch, team }) => {
  const legalUnitName = useLegalUnitName(team);
  const { isTestEnvironment } = useGlobalContext();

  const [modalMode, setModalMode] = useState(team.id ? PresentationMode.Edit : PresentationMode.Add);
  const [editedTeamData, setEditedTeamData] = useState(
    mapTeamToTeamData(team, canTeamResponsibleTypeBeSet(legalUnitName), modalMode)
  );
  const [canSubmit, setCanSubmit] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();

  /**
   * This makes the cancel button navigate back to search page, since in the "ADD" mode cancel should mean cancel the operation to create a new team
   */
  const cancelFunction = () => {
    modalMode === PresentationMode.Add ? navigate('/') : closeView();
  };

  const getCommonFields = (propertyName, isMandatory) => {
    const handleChange = e => {
      setEditedTeamData({
        ...editedTeamData,
        [e.target.name]: e.target.value,
      });
    };

    return {
      name: propertyName,
      key: propertyName,
      onChange: handleChange,
      value: editedTeamData[propertyName] ?? '',
      error: errors[propertyName],
      isMandatory: isMandatory,
    };
  };

  const onSubmit = (event, editedTeamData) => {
    event.preventDefault();

    if (modalMode === PresentationMode.Edit) {
      ServiceClient.updateTeamInfo(
        getChangedFieldsForTeam(editedTeamData, team),
        mapEditedTeamDataForApi(editedTeamData),
        data => {
          teamPageDispatch({
            type: teamPageReducerTypes.updateTeamInfo,
            payload: data,
          });
          closeView();
        },
        (errorMessage, errorData) => {
          setModalMode(PresentationMode.Error);
          setCanSubmit(false);
          if (errorData?.code === ResultErrorCode.TeamNotFound) {
            setErrors(prev => ({ ...prev, parentTeamCodePrefix: formatMessage(Messages.teamInfo_parentTeamNotFound) }));
          } else if (errorData?.code === ResultErrorCode.RecursiveParentTeam) {
            setErrors(prev => ({
              ...prev,
              parentTeamCodePrefix: formatMessage(Messages.teamInfo_parentTeamRecursive),
            }));
          } else {
            setErrors(prev => ({
              ...prev,
              [teamInfoFormId]: createServerErrorMessage(errorMessage, teamInfoFormId).message,
            }));
          }
        }
      );
    } else if (modalMode === PresentationMode.Add) {
      teamPageDispatch({
        type: teamPageReducerTypes.updateTeamInfo,
        payload: editedTeamData,
      });
      closeView();
    }
  };

  const validateTeamInfo = useCallback(() => {
    areFieldsValid(editedTeamData, team, modalMode, canTeamNameBeSet(legalUnitName)).then(validationErrors => {
      setErrors(prev => ({ ...prev, ...validationErrors }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedTeamData]);

  // Code prefix is validated separately to not call API when code prefix field was not changed
  const validateCodePrefix = useCallback(() => {
    validateCodePrefixInput(editedTeamData.codePrefix, modalMode).then(result => {
      setErrors(prev => ({ ...prev, codePrefix: result }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedTeamData.codePrefix]);

  // User ID is validated separately
  const validateUserId = useCallback(() => {
    validateUserIdInput(editedTeamData.userId).then(result => {
      setErrors(prev => ({ ...prev, userId: result }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedTeamData.userId]);

  useDebouncedEffect(validateTeamInfo, 500);
  useDebouncedEffect(validateCodePrefix, 500);
  useDebouncedEffect(validateUserId, 500);

  useEffect(() => {
    setCanSubmit(
      canFormBeSubmitted(errors) &&
        (modalMode === PresentationMode.Add ? true : getChangedFieldsForTeam(editedTeamData, team) > 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return (
    <DraggableModal
      titleText={
        modalMode === PresentationMode.Add
          ? formatMessage(Messages.teamInfo_titleTextNewTeam)
          : formatMessage(Messages.teamInfo_titleTextEditTeam, { codePrefix: team.codePrefix })
      }
      closeView={cancelFunction}
      buttons={
        <TeamInfoButtons
          closeView={cancelFunction}
          closeError={() => {
            setModalMode(team.id ? PresentationMode.Edit : PresentationMode.Add);
            setErrors({});
          }}
          canSubmit={canSubmit}
          modalMode={modalMode}
        />
      }>
      <div className="teamPage-teamInfo-container">
        <Form
          formId={teamInfoFormId}
          onSubmit={e => {
            onSubmit(e, editedTeamData);
          }}
          disabled={modalMode === PresentationMode.Error}>
          {modalMode === PresentationMode.Add && (
            <div className="teamPage-teamInfo-codePrefix-container">
              <TextInput label={formatMessage(Messages.common_codePrefix)} {...getCommonFields('codePrefix', true)} />
              {canTeamResponsibleTypeBeSet(legalUnitName) && isTestEnvironment && (
                <Select
                  label={formatMessage(Messages.teamInfo_teamResponsibleType)}
                  defaultValue={editedTeamData.responsibleType ?? ResponsibleType.Internal}
                  onChange={e => {
                    setEditedTeamData({ ...editedTeamData, responsibleType: e.target.value });
                  }}
                  options={getTeamResponsibleTypeOptions()}
                />
              )}
            </div>
          )}
          <TextInput
            cssClasses="teamPage-teamInfo-userId"
            label={formatMessage(Messages.common_userId)}
            {...getCommonFields('userId')}
          />
          {canTeamNameBeSet(legalUnitName) ? (
            <TextInput
              cssClasses="teamPage-teamInfo-name"
              label={formatMessage(Messages.common_teamName)}
              {...getCommonFields('name', true)}
            />
          ) : (
            areDecoupledFunctionsAvailableFunction(team) && (
              <TextInput
                cssClasses="teamPage-teamInfo-name"
                label={formatMessage(Messages.common_name)}
                {...getCommonFields('commonAgentName', true)}
              />
            )
          )}

          <ParentTeamCodePrefix team={team} editedTeamData={editedTeamData} setEditedTeamData={setEditedTeamData} />

          {canEditBankAccount(team) && (
            <>
              <TextInput
                cssClasses="teamPage-teamInfo-bankNumber"
                label={formatMessage(Messages.common_bankAccount)}
                {...getCommonFields('bankAccountNo', isBankAccountMandatory(team))}
                onChange={e => {
                  if (team.bankAccount?.number !== null && e.target.value?.trim() === '') {
                    setEditedTeamData({
                      ...editedTeamData,
                      bankAccountNo: null,
                      bankAccountType: null,
                    });
                  } else {
                    setEditedTeamData({
                      ...editedTeamData,
                      bankAccountNo: e.target.value,
                    });
                  }
                }}
              />
              <BankAccountTypeSelector
                bankAccountType={editedTeamData.bankAccountType}
                onChange={type => {
                  setEditedTeamData({
                    ...editedTeamData,
                    bankAccountType: type,
                  });
                }}
              />
            </>
          )}

          {canTeamMobileNumberBeSet(legalUnitName) && (
            <TextInput
              cssClasses="teamPage-teamInfo-mobileNumber"
              label={formatMessage(Messages.common_mobile)}
              {...getCommonFields('mobileNo', false)}
            />
          )}

          <TextInput
            cssClasses="teamPage-teamInfo-email"
            label={formatMessage(Messages.common_email)}
            {...getCommonFields('email')}
          />

          <TextInput
            cssClasses="teamPage-teamInfo-legalNumber"
            label={formatMessage(Messages.common_legalNumber)}
            {...getCommonFields('legalNumber')}
          />

          <ErrorMessage message={errors[teamInfoFormId]} />
        </Form>
      </div>
    </DraggableModal>
  );
};

export default TeamInfo;
